/**
 * Partner Registration Service
 * Handles partner and shop onboarding/registration
 */

import { Partner, MembershipPlan, PartnerType } from '../types/partner';
import { Shop } from '../types/shop';
import { partnerRepository } from '../../data/partnerRepository';
import { logger } from '../../infrastructure/logger';
import { isValidSlug, isReservedSlug } from '../../utils/validation';
import { calculateExpiryDate } from '../../utils/dateHelpers';

const MEMBERSHIP_CONFIG: Record<MembershipPlan, { price: number; commissionRate: number; maxProducts: number }> = {
    free: { price: 0, commissionRate: 20, maxProducts: 10 },
    basic: { price: 5000, commissionRate: 15, maxProducts: 25 },
    pro: { price: 15000, commissionRate: 10, maxProducts: 75 },
    premium: { price: 30000, commissionRate: 5, maxProducts: -1 }
};

interface CreatePartnerRequest {
    firebaseUid: string;
    email: string;
    name: string;
    phone: string;
    dni: string;
    type: PartnerType;
    businessName?: string;
    membership: MembershipPlan;
}

interface CreateShopRequest {
    partnerId: string;
    name: string;
    slug: string;
    description: string;
    categories: string[];
    contactInfo: {
        phone: string;
        email: string;
        whatsapp?: string;
        address?: string;
    };
}

interface RegistrationResult {
    success: boolean;
    partnerId?: string;
    shopId?: string;
    error?: string;
}

export const partnerRegistrationService = {
    /**
     * Check if slug is valid and available
     */
    async checkSlugAvailability(slug: string): Promise<{ available: boolean; error?: string }> {
        if (!isValidSlug(slug)) {
            return { available: false, error: "El slug no es válido" };
        }
        if (isReservedSlug(slug)) {
            return { available: false, error: "Este nombre está reservado" };
        }
        const isAvailable = await partnerRepository.isSlugAvailable(slug);
        return { available: isAvailable };
    },

    /**
     * Register partner and shop together
     */
    async registerPartnerAndShop(
        partnerData: CreatePartnerRequest,
        shopData: Omit<CreateShopRequest, 'partnerId'>
    ): Promise<RegistrationResult> {
        try {
            // Validate slug
            const slugCheck = await this.checkSlugAvailability(shopData.slug);
            if (!slugCheck.available) {
                return { success: false, error: slugCheck.error || "El slug de la tienda no es válido" };
            }

            // Check if partner already exists
            const existingPartner = await partnerRepository.getPartnerByFirebaseUid(partnerData.firebaseUid);
            if (existingPartner) {
                return { success: false, error: "Ya existe una cuenta de vendedor asociada a este usuario" };
            }

            const config = MEMBERSHIP_CONFIG[partnerData.membership];
            const now = new Date().toISOString();

            // Create partner
            const partner: Omit<Partner, 'id'> = {
                firebaseUid: partnerData.firebaseUid,
                email: partnerData.email,
                name: partnerData.name,
                phone: partnerData.phone,
                dni: partnerData.dni,
                type: partnerData.type,
                businessName: partnerData.businessName,
                membership: partnerData.membership,
                membershipStartedAt: now,
                membershipExpiresAt: partnerData.membership === 'free' ? undefined : calculateExpiryDate(now),
                autoRenew: false,
                balance: 0,
                pendingBalance: 0,
                totalSales: 0,
                totalCommissionPaid: 0,
                commissionRate: config.commissionRate,
                isVerified: false,
                status: partnerData.membership === 'free' ? 'pending' : 'pending_documents',
                contactInfo: {
                    phone: partnerData.phone,
                    email: partnerData.email
                },
                notificationPreferences: {
                    email: true,
                    sms: false,
                    push: true
                },
                createdAt: now,
                updatedAt: now
            };

            const partnerId = await partnerRepository.createPartner(partner);

            // Create shop
            const shop: Shop = {
                id: '',
                partnerId,
                name: shopData.name,
                slug: shopData.slug.toLowerCase(),
                description: shopData.description,
                shortDescription: shopData.description.substring(0, 150),
                categories: shopData.categories,
                contactInfo: shopData.contactInfo,
                theme: {
                    bannerPosition: 'top'
                },
                stats: {
                    totalSales: 0,
                    totalRevenue: 0,
                    totalProducts: 0,
                    totalServices: 0,
                    rating: 0,
                    reviewCount: 0,
                    followers: 0,
                    totalViews: 0
                },
                seoTitle: shopData.name,
                seoDescription: shopData.description.substring(0, 160),
                settings: {
                    allowReviews: true,
                    allowQuestions: true,
                    deliveryOptions: ['pickup', 'delivery']
                },
                status: 'pending',
                isVerified: false,
                featured: false,
                createdAt: now,
                updatedAt: now
            };

            const shopId = await partnerRepository.createShop(shop);

            // Update partner with shopId
            await partnerRepository.updatePartner(partnerId, { shopId } as Partial<Partner>);

            return { success: true, partnerId, shopId };
        } catch (error) {
            logger.error('[PartnerRegistrationService] Error registering partner', error);
            return { success: false, error: "Error al registrar el vendedor" };
        }
    },

    /**
     * Get membership limits for a plan
     */
    getMembershipLimits(membership: MembershipPlan): { maxProducts: number; commissionRate: number } {
        const config = MEMBERSHIP_CONFIG[membership];
        return {
            maxProducts: config.maxProducts,
            commissionRate: config.commissionRate
        };
    }
};
